import { fetcher } from '@chipinside/fetcher'
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { isEqual } from 'lodash'
import isEmpty from 'lodash/isEmpty'
import { shallowEqual, useSelector } from 'react-redux'

import { Plan } from '#/@types/backend'
import { endpoint } from '#/store/services/endpoints'
import { sanitizeError } from '#/utils/SanitizeError'

type FarmState = {
  loading: boolean
  farm: Record<string, any>
}

const defaultState: FarmState = {
  farm: {},
  loading: false,
}

export const loadFarm = createAsyncThunk(
  'farm/loadFarm',
  async ({ farmSlug }: { farmSlug: string }, thunkAPI) => {
    if (!isEmpty(farmSlug)) {
      try {
        const controller = endpoint.farm.self({ farm: farmSlug })
        // @ts-ignore
        const { data } = await fetcher<any>({ controller })
        return { data }
      } catch (error: any) {
        return thunkAPI.rejectWithValue({ error: sanitizeError(error) })
      }
    }

    return thunkAPI.rejectWithValue({
      message: 'load farm info not handled',
      slug: farmSlug,
    })
  },
)

export const farmSlice = createSlice({
  extraReducers: builder => {
    builder
      .addCase(loadFarm.pending, state => {
        state.loading = true
      })
      .addCase(loadFarm.rejected, (state, { payload }: any) => {
        state.loading = false
        throw payload.error
      })
      .addCase(loadFarm.fulfilled, (state, { payload }) => {
        state.farm = payload?.data
        state.loading = false
      })
  },
  initialState: defaultState,
  name: 'farm',
  reducers: {
    resetFarm(state) {
      state.farm = {}
    },
    setFarmAssistant(state, { payload }) {
      state.farm = {
        ...state.farm,
        vic_assistant: payload?.assistant,
      }
    },
    setFarmNPSCampaign(state) {
      state.farm.has_active_campaign = null
    },
  },
  selectors: {
    getFarm: state => state.farm,
    getFarmAssistant: state => state.farm.vic_assistant ?? null,
    getFarmLocation: state => state.farm?.address ?? {},
    getFarmName: state => state.farm.name ?? null,
    getFarmPlan: state => state.farm.plan ?? null,
    getFarmSlug: state => state.farm.slug ?? null,
    getFarmTimezone: state => state.farm.timezone ?? 'America/Sao_Paulo',
    getPregnancyActive: state => ({
      secondPregnancy: state.farm.second_pregnancy_confirmation_active ?? null,
      thirdPregnancy: state.farm.third_pregnancy_confirmation_active ?? null,
    }),
  },
})

export const {
  actions: { resetFarm, setFarmAssistant, setFarmNPSCampaign },
  selectors: {
    getFarm,
    getFarmAssistant,
    getFarmLocation,
    getFarmName,
    getFarmPlan,
    getFarmSlug,
    getFarmTimezone,
    getPregnancyActive,
  },
} = farmSlice

export const useFarm = () => useSelector(getFarm, isEqual)
export const useFarmLocation = () => useSelector(getFarmLocation, isEqual)
export const useFarmSlug = () => useSelector(getFarmSlug, shallowEqual)
export const useFarmName = () => useSelector(getFarmName, shallowEqual)
export const useFarmTimezone = () => useSelector(getFarmTimezone, shallowEqual)
export const useFarmAssistant = () => useSelector(getFarmAssistant, shallowEqual)

export const usePregnancyActive = () => useSelector(getPregnancyActive, isEqual)

export const useFarmPlan = (): {
  name: string
  slug: Plan
  isMonitoring: boolean
  isCowConnect: boolean
} => {
  const plan = useSelector(getFarmPlan, shallowEqual)
  if (!plan) return {} as any

  const isMonitoring = plan?.slug === 'monitoring'
  return { ...plan, isCowConnect: !isMonitoring, isMonitoring }
}

export default farmSlice.reducer
